import { useCallback, useId } from 'react';
import styled from 'styled-components';
import { useSavePrcPayrollMutation, type PrcVersion, type PrcVersionPayload } from '@shared/api';
import {
  NBSP,
  ScrollRegion,
  VisuallyHidden,
  blockNumber,
  fleetType,
  normalizeDecimal,
  typo,
} from '@entities/fleet';
import { buildPayrollLayout, laterYears, multiplyDecimal } from '@entities/prc';
import { FieldHint, FieldLabel } from '@widgets/FleetRegistry/ui';
import type { PrcAccess } from '../access';
import { PROFESSIONS, count, formatNumber } from '../format';
import {
  CompactTable,
  GridInput,
  NARROW,
  NumTable,
  editorRows,
  ReadValue,
  Section,
  SectionHead,
  SectionNote,
  SectionTitle,
} from '../ui';
import { EditorNotices, EditorSaveBar, ReadOnlyNote } from './EditorChrome';
import { useBlockEditor, type BlockEditor } from './useBlockEditor';

const pick = (v: PrcVersion) => v.payroll;
const layoutOf = (v: PrcVersion) =>
  buildPayrollLayout(v.payroll, v.params.years, v.params.base_year);

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(5)}`};

  @container prc (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container prc (max-width: ${NARROW}) {
    grid-template-columns: minmax(0, 1fr);
  }

  & input {
    text-align: right;
  }
`;

const Unit = styled.span`
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ProfTable = styled(NumTable)`
  ${editorRows}

  tbody th {
    min-width: 220px;
    overflow-wrap: anywhere;
  }

  td.cell {
    min-width: 116px;
  }
`;

function Field({
  editor,
  k,
  label,
  unit,
  hint,
}: {
  editor: BlockEditor;
  k: string;
  label: string;
  unit: string;
  hint?: string;
}) {
  const b = editor.bind(k);
  const err = editor.error(k);
  return (
    <div>
      <FieldLabel htmlFor={b.id}>
        <span>{label}</span>
        <Unit>{unit}</Unit>
      </FieldLabel>
      {editor.editable ? (
        <GridInput
          {...b}
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          aria-describedby={
            [hint ? `${b.id}-hint` : null, err ? `${b.id}-error` : null]
              .filter(Boolean)
              .join(' ') || undefined
          }
        />
      ) : (
        <ReadValue>{editor.values[k] || '—'}</ReadValue>
      )}
      {hint && <FieldHint id={`${b.id}-hint`}>{hint}</FieldHint>}
      {err && <VisuallyHidden id={`${b.id}-error`}>{err}</VisuallyHidden>}
    </div>
  );
}

function Cell({ editor, k, label }: { editor: BlockEditor; k: string; label: string }) {
  const err = editor.error(k);
  if (!editor.editable) return <ReadValue>{editor.values[k] || '—'}</ReadValue>;
  return (
    <>
      <GridInput
        {...editor.bind(k)}
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        aria-label={label}
      />
      {err && <VisuallyHidden id={`${editor.domId(k)}-error`}>{err}</VisuallyHidden>}
    </>
  );
}

/**
 * Вкладка «ФОТ механизаторов»: множитель «на руки», НДФЛ, отпускные, взносы,
 * индексация часовой ставки по годам и профессии. «На руки» профессии =
 * база × множитель (ячейка W2 книги).
 */
export function PayrollTab({
  payload,
  access,
  onDirtyChange,
  onCompare,
}: {
  payload: PrcVersionPayload;
  access: PrcAccess;
  onDirtyChange: (count: number) => void;
  onCompare: () => void;
}) {
  const uid = useId();
  const [savePayroll] = useSavePrcPayrollMutation();
  const variantId = payload.variant?.id;
  const save = useCallback(
    (args: { id: string; block: PrcVersion['payroll']; expected_updated_at: string }) =>
      savePayroll({ ...args, variant_id: variantId }).unwrap(),
    [savePayroll, variantId],
  );
  const editor = useBlockEditor({
    payload,
    canWrite: access.canWrite,
    pick,
    layout: layoutOf,
    save,
    onDirtyChange,
    idPrefix: uid,
  });
  const l = editor.layout;
  const { years, base_year: baseYear } = editor.base.params;
  const later = laterYears(years, baseYear);
  const multiplier = normalizeDecimal(editor.values[l.multiplier] ?? '') ?? '1';

  return (
    <>
      <ReadOnlyNote status={editor.base.status} canWrite={access.canWrite} />
      <EditorNotices editor={editor} lead="ФОТ сохранён" onCompare={onCompare} />

      <Section aria-labelledby={`${uid}-rates-h`}>
        <SectionHead>
          <SectionTitle id={`${uid}-rates-h`}>
            <span className="n" aria-hidden="true">
              {blockNumber(0)}
            </span>
            Ставки
          </SectionTitle>
          <SectionNote>
            {typo(
              'Часовая ставка = «на руки» / (1 − НДФЛ) × человек / часы; × (1 + отпускные) × (1 + взносы)',
            )}
          </SectionNote>
        </SectionHead>
        <Fields>
          <Field
            editor={editor}
            k={l.multiplier}
            label="Множитель «на руки»"
            unit="×"
            hint={typo(
              'Ячейка W2 книги: «на руки» = база × множитель. Смысл уточняется у экономиста',
            )}
          />
          <Field editor={editor} k={l.ndfl} label="НДФЛ" unit="%" hint="В книге 13 %" />
          <Field
            editor={editor}
            k={l.vacation}
            label="Резерв на отпуск"
            unit="%"
            hint="В книге 8 %"
          />
          <Field
            editor={editor}
            k={l.contributions}
            label="Страховые взносы"
            unit="%"
            hint={typo('ПФР 22 + ФСС 2,9 + ФФОМС 5,1 + доптариф 4 + НС 1,2 = 35,2 %')}
          />
        </Fields>
      </Section>

      <Section aria-labelledby={`${uid}-index-h`}>
        <SectionHead>
          <SectionTitle id={`${uid}-index-h`}>
            <span className="n" aria-hidden="true">
              {blockNumber(1)}
            </span>
            Индексация часовой ставки
          </SectionTitle>
          <SectionNote>К прошлому году, %</SectionNote>
        </SectionHead>
        <ScrollRegion label="Индексация часовой ставки по годам">
          <CompactTable>
            <thead>
              <tr>
                {later.map((y) => (
                  <th key={y} scope="col" className="num">
                    {y}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {later.map((y) => {
                  const k = l.indexCells[y];
                  return (
                    <td key={y} className="num">
                      {k ? (
                        <Cell editor={editor} k={k} label={`Индексация ставки ${y}, %`} />
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </CompactTable>
        </ScrollRegion>
      </Section>

      <Section aria-labelledby={`${uid}-prof-h`}>
        <SectionHead>
          <SectionTitle id={`${uid}-prof-h`}>
            <span className="n" aria-hidden="true">
              {blockNumber(2)}
            </span>
            Профессии
          </SectionTitle>
          <SectionNote>
            {typo(
              `${count(l.professions.length, PROFESSIONS)} · ставку модели находят по должности механизатора в паспорте, поэтому названия здесь не меняются`,
            )}
          </SectionNote>
        </SectionHead>
        <ScrollRegion label="Профессии блока ФОТ">
          <ProfTable>
            <thead>
              <tr>
                <th scope="col" className="sticky">
                  Профессия
                </th>
                <th scope="col" className="num">
                  «На руки» база, ₽/мес
                </th>
                <th scope="col" className="num">
                  «На руки» × множитель, ₽/мес
                </th>
                <th scope="col" className="num">
                  Человек на машине
                </th>
                <th scope="col" className="num">
                  Часов в месяц
                </th>
              </tr>
            </thead>
            <tbody>
              {l.professions.map((p) => {
                const base = normalizeDecimal(editor.values[p.baseCell] ?? '');
                const onHand = base ? multiplyDecimal(base, multiplier) : null;
                return (
                  <tr key={p.id} className="row">
                    <th scope="row" className="sticky">
                      {p.name}
                    </th>
                    <td className="cell num">
                      <Cell
                        editor={editor}
                        k={p.baseCell}
                        label={`${p.name}: «на руки» база, ₽ в месяц`}
                      />
                    </td>
                    <td className="num muted text">{onHand ? formatNumber(onHand, 2) : '—'}</td>
                    <td className="cell num">
                      <Cell
                        editor={editor}
                        k={p.countCell}
                        label={`${p.name}: человек на машине`}
                      />
                    </td>
                    <td className="cell num">
                      <Cell editor={editor} k={p.hoursCell} label={`${p.name}: часов в месяц`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ProfTable>
        </ScrollRegion>
        <SectionNote>
          {typo(
            `Пустое «человек на машине» — 1. «На руки» × множитель${NBSP}— как колонка W книги, считается на сервере при пересчёте; здесь — для сверки.`,
          )}
        </SectionNote>
      </Section>

      <EditorSaveBar editor={editor} />
    </>
  );
}
